/**
 * Cable-run cost maths — single implementation.
 *
 * The material + termination + pull-labour formula was previously
 * written out three times (project-cost-rollup, computePlanCostTotals
 * in project-plan.service, and the total-metres helper in
 * project-cable.service). Three copies meant `quantity` would have had
 * to be threaded through three times, with a silent under-count of the
 * project's cost the moment one of them drifted. It lives here now.
 *
 * Pure — no Firestore, no React. Cable cost is CLIENT-ONLY today (no
 * functions/ mirror), so this module has no lock-step partner; if a
 * server-side cable price ever lands, promote this to `shared/src/`
 * rather than copying it.
 *
 * Formula per run (all amounts ex GST, AUD):
 *
 *   adjustedMeters   = lengthMeters × wastageFactor × quantity
 *   material         = adjustedMeters × cablePrice
 *   terminations     = 2 × terminationEach × quantity   (one each end)
 *   pullLabourHours  = lengthMeters × quantity × pullLabourMinPerMeter / 60
 *
 * `quantity` is the number of identical cables in the run and defaults
 * to 1, so every run written before the field existed prices exactly as
 * it did before.
 */

import { LABOUR_RATE_PER_HOUR, LABOUR_SELL_RATE_PER_HOUR } from '@/config/sla.config';
import type { CableRun } from '@/types/project.types';

/** Number of identical cables in the run. Clamped to a whole number
 *  ≥ 1: a 0 or negative quantity is data corruption, and pricing it as
 *  zero would silently drop the run from the rollup rather than
 *  surfacing the bad value. */
export function cableRunQuantity(run: Pick<CableRun, 'quantity'>): number {
  const q = run.quantity;
  if (typeof q !== 'number' || !Number.isFinite(q)) return 1;
  return Math.max(1, Math.round(q));
}

/** Metres of cable actually consumed — length × wastage × quantity.
 *  This is the number the BOM orders against, not the run length. */
export function cableRunAdjustedMeters(run: CableRun): number {
  const length = run.lengthMeters ?? 0;
  const wastage = run.priceSnapshot?.wastageFactor ?? 1;
  return length * wastage * cableRunQuantity(run);
}

export interface CableRunCost {
  cost: number;
  sell: number;
}

/** Component-by-component split, for the detail panel's breakdown
 *  table. `cableRunCost` is just this with the components summed —
 *  keeping one formula means the panel's line items can never add up to
 *  something different from the rollup's total. */
export interface CableRunCostBreakdown extends CableRunCost {
  /** Cables in the run — every component below is already ×qty. */
  quantity: number;
  /** lengthMeters × wastageFactor × quantity. */
  adjustedMeters: number;
  cableMaterialCost: number;
  cableMaterialSell: number;
  /** 2 terminations per cable. */
  terminationCount: number;
  terminationsCost: number;
  terminationsSell: number;
  pullLabourMinutes: number;
  pullLabourCost: number;
  pullLabourSell: number;
  /** Margin %, or null when sell is zero (UI renders an em-dash). */
  margin: number | null;
}

const ZERO_BREAKDOWN: CableRunCostBreakdown = {
  cost: 0,
  sell: 0,
  quantity: 1,
  adjustedMeters: 0,
  cableMaterialCost: 0,
  cableMaterialSell: 0,
  terminationCount: 0,
  terminationsCost: 0,
  terminationsSell: 0,
  pullLabourMinutes: 0,
  pullLabourCost: 0,
  pullLabourSell: 0,
  margin: null,
};

/** Full cost breakdown for one run. Returns zeroes when the run carries
 *  no price snapshot (defensive — priceSnapshot is non-optional on the
 *  type, but legacy docs predate it). Does NOT apply the
 *  Archived-is-free rule; that's a policy call the caller owns. */
export function cableRunCostBreakdown(run: CableRun): CableRunCostBreakdown {
  const snap = run.priceSnapshot;
  if (!snap) return ZERO_BREAKDOWN;

  const quantity = cableRunQuantity(run);
  const length = run.lengthMeters ?? 0;
  const wastage = snap.wastageFactor ?? 1;
  const adjustedMeters = length * wastage * quantity;

  const cableMaterialCost = adjustedMeters * (snap.cableCostPrice ?? 0);
  const cableMaterialSell = adjustedMeters * (snap.cableSellPrice ?? 0);
  // Two terminations per cable — one each end — so a ×2 run terminates
  // four times.
  const terminationCount = 2 * quantity;
  const terminationsCost = terminationCount * (snap.terminationCostEach ?? 0);
  const terminationsSell = terminationCount * (snap.terminationSellEach ?? 0);
  // Pull labour scales on RUN length × cable count, not on the
  // wastage-inflated figure — you don't pay a sparky to pull offcuts.
  const pullLabourMinutes = length * quantity * (snap.pullLabourMinPerMeter ?? 0);
  const pullLabourCost = (pullLabourMinutes / 60) * LABOUR_RATE_PER_HOUR;
  const pullLabourSell = (pullLabourMinutes / 60) * LABOUR_SELL_RATE_PER_HOUR;

  const cost = cableMaterialCost + terminationsCost + pullLabourCost;
  const sell = cableMaterialSell + terminationsSell + pullLabourSell;

  return {
    cost,
    sell,
    quantity,
    adjustedMeters,
    cableMaterialCost,
    cableMaterialSell,
    terminationCount,
    terminationsCost,
    terminationsSell,
    pullLabourMinutes,
    pullLabourCost,
    pullLabourSell,
    margin: sell > 0 ? ((sell - cost) / sell) * 100 : null,
  };
}

/** Planned cost + sell for one run. */
export function cableRunCost(run: CableRun): CableRunCost {
  const { cost, sell } = cableRunCostBreakdown(run);
  return { cost, sell };
}
